import { type ComponentType, useCallback, useRef } from "react";
import { useRouter } from "solito/router";
import { match, P } from "ts-pattern";
import { isWeb } from "@medbillai/ui";
import { loginUrl } from "@medbillai/utils/urls";
import { createFlowTracker } from "../../lib/analytics/flowEvents";
import type { FlowState, FlowTransition } from "../../lib/flow-engine";
import { useSignUpOnboardingStore } from "../../lib/sign-up-onboarding/signUpOnboardingStore";
import { SingularEvents, singularService } from "../../lib/singular";
import { onboardingStorage } from "../../lib/storage/instances";
import { type AuthHandoff, ONBOARDING_VERSION, type OnboardingConfig, OnboardingFlow, type OnboardingTransitionProps, Screen } from "./OnboardingFlow";
import type { Intent } from "./types";

// Maps the v2 intent question onto the v1 `features` strings the home screen
// personalizes on (see lib/auth/utils.ts: isBillCheckingTargeted /
// isBenefitsTargeted). `other` has no app-affecting feature, so it is omitted.
const INTENT_TO_FEATURE: Partial<Record<Intent, string>> = {
  bill: "FIX_BILLING_ERRORS",
  learn: "MAXIMIZE_BENEFITS"
};
const track = createFlowTracker("Onboarding", {
  onboardingVersion: ONBOARDING_VERSION
});

// Every outcome is tracked. `.exhaustive()` forces an arm when the config
// gains an outcome, and the tracker's constraints force the EventTypes entry
// and reject undeclared properties. Back/plain-continue funnel into the
// shared OnboardingBackOutcome / OnboardingContinueOutcome.
const trackTransitionEvent = (event: FlowTransition<OnboardingConfig>) => match(event).with({
  from: {
    tag: P.select()
  },
  outcome: {
    tag: "back"
  }
}, from => track.back(from)).with({
  from: {
    tag: "Intent"
  },
  outcome: {
    tag: "selected"
  }
}, ({
  outcome
}) => {
  track.outcome("Intent", "selected", {
    intent: outcome.data.intent
  });
  // Singular is ad attribution, not Amplitude — it keeps its own event.
  singularService.event(SingularEvents.onboardingQuestionAnswered, {
    questionId: "intent",
    selections: outcome.data.intent
  });
}).with({
  from: {
    tag: "Resuming"
  },
  outcome: {
    tag: "ready"
  }
}, ({
  outcome
}) => track.outcome("Resuming", "ready", {
  intent: outcome.data.intent
})).with({
  from: {
    tag: "Education"
  },
  outcome: {
    tag: "continue"
  }
}, () => track.continue("Education")).with({
  from: {
    tag: "Notifications"
  },
  outcome: {
    tag: "configured"
  }
}, ({
  outcome
}) => track.outcome("Notifications", "configured", {
  enabled: outcome.data.enabled
})).with({
  from: {
    tag: "Notifications"
  },
  outcome: {
    tag: "skipped"
  }
}, () => track.outcome("Notifications", "skipped")).with({
  from: {
    tag: "ValueProps"
  },
  outcome: {
    tag: P.select(P.not("back"))
  }
}, outcomeTag => track.outcome("ValueProps", outcomeTag)).exhaustive();

// Screens with extra properties get dedicated arms; the rest fall through to
// the catch-all, whose tag constraint still demands an EventTypes entry.
const trackScreenEntered = (state: FlowState<OnboardingConfig>) => match(state).with({
  tag: P.union("Education", "Notifications")
}, ({
  tag,
  data
}) => track.screen(tag, {
  intent: data.intent
})).with({
  tag: P.select()
}, tag => track.screen(tag)).exhaustive();
const computeInitial = (phase: OnboardingPhase): FlowState<OnboardingConfig> => {
  if (phase === "preauth") {
    return isWeb ? Screen.Intent() : Screen.ValueProps();
  }
  return Screen.Resuming();
};
export type OnboardingPhase = "preauth" | "postauth";
export type OnboardingShellProps = {
  phase: OnboardingPhase;
  /** Per-platform screen-transition wrapper — see `OnboardingFlow`. */
  Transition: ComponentType<OnboardingTransitionProps>;
};

/**
 * Glue between the pure `OnboardingFlow` and the rest of the system: picks
 * initial state from platform, wires analytics, owns the stash lifecycle,
 * and performs redirects on terminal handoff.
 */
export function OnboardingShell({
  phase,
  Transition
}: OnboardingShellProps) {
  const {
    push,
    replace
  } = useRouter();
  const {
    setIsSignUpOnboarding,
    setUserSegmentationData
  } = useSignUpOnboardingStore();
  const handleAuthHandoff = useCallback((event: AuthHandoff) => {
    match(event).with({
      tag: "awaitingAccountCreation"
    }, ({
      data
    }) => {
      void onboardingStorage.set("intent", data.intent);
      // Also persist the intent as a v1 `features` value so the home
      // screen can personalize. `login.tsx` reads this off the store and
      // passes it to `sendMagicLink`, which upserts UserSegmentationData.
      const feature = INTENT_TO_FEATURE[data.intent];
      if (feature) {
        setUserSegmentationData({
          features: [feature]
        });
      }
      setIsSignUpOnboarding(true);
      push(loginUrl({
        variant: data.intent
      }));
    }).with({
      tag: "exitToLogin"
    }, () => {
      push(loginUrl());
    }).exhaustive();
  }, [push, setIsSignUpOnboarding, setUserSegmentationData]);
  const handleComplete = useCallback(() => {
    void onboardingStorage.remove("intent");
    setIsSignUpOnboarding(false);
    replace("/");
  }, [replace, setIsSignUpOnboarding]);

  // Auth-handoff outcomes are self-transitions: the engine commits a fresh
  // same-tag state while the shell redirects, so an unfiltered onStateLoaded
  // would re-fire the screen's StepShown as the user leaves the flow.
  const lastTrackedTagRef = useRef<FlowState<OnboardingConfig>["tag"] | null>(null);
  const handleStateLoaded = useCallback((state: FlowState<OnboardingConfig>) => {
    if (state.tag === lastTrackedTagRef.current) {
      return;
    }
    lastTrackedTagRef.current = state.tag;
    trackScreenEntered(state);
  }, []);
  return <OnboardingFlow initial={computeInitial(phase)} ctx={{
    isWeb
  }} onAuthHandoff={handleAuthHandoff} onComplete={handleComplete} onTransition={trackTransitionEvent} onStateLoaded={handleStateLoaded} Transition={Transition} data-sentry-element="OnboardingFlow" data-sentry-component="OnboardingShell" data-sentry-source-file="OnboardingShell.tsx" />;
}